import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pie, Bar, Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, ArcElement, Legend } from "chart.js";
import { getBrokeragesList, loadBrokerages } from "../../store/brokerages"; // Adjust path if needed
import "./QuoteyDashboard.css";
import { getSortIndicator, sortData } from './sortingUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, ArcElement, Legend);

const BrokerageTab = () => {
  const dispatch = useDispatch();
  const rawBrokerages = useSelector(getBrokeragesList);

  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
  const [processedData, setProcessedData] = useState({
    activeBrokerages: 0, inactiveBrokerages: 0, newBrokeragesByMonth: [],
    brokersPerBrokerage: [], totalBrokers: 0
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all', 'active', 'inactive'

  useEffect(() => {
    dispatch(loadBrokerages());
  }, [dispatch]);

  const filteredBrokerages = useMemo(() => {
    if (!rawBrokerages) return [];
    return rawBrokerages.filter(b => {
        const matchesSearch = searchTerm === "" ||
                              (b.name && b.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                              (b.code && b.code.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesStatus = statusFilter === "all" ||
                              (statusFilter === "active" && b.is_active_subscription) ||
                              (statusFilter === "inactive" && !b.is_active_subscription);
        return matchesSearch && matchesStatus;
    });
  }, [rawBrokerages, searchTerm, statusFilter]);

  const sortedAndFilteredBrokerages = useMemo(() => {
    return sortData(filteredBrokerages, sortConfig);
  }, [filteredBrokerages, sortConfig]);

  // [UPDATED] Using the improved new brokerage tracking function
  const processNewBrokeragesByMonth = (data) => {
    const monthLabels = [];
    const counts = [];
    const now = new Date();

    for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        monthLabels.push(d.toLocaleString('default', { month: 'short', year: '2-digit' }));
        counts.push(0);
    }
    
    const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 11, 1);

    data.forEach(b => {
        if (!b._id) return;
        const creationTimestamp = parseInt(b._id.substring(0, 8), 16) * 1000;
        const createdAt = new Date(creationTimestamp);
        
        if (createdAt >= twelveMonthsAgo) {
            const year = createdAt.getFullYear();
            const month = createdAt.getMonth();
            const monthsDiff = (now.getFullYear() - year) * 12 + (now.getMonth() - month);
            const index = 11 - monthsDiff;
            if (index >= 0 && index < 12) {
                counts[index]++;
            }
        }
    });
    return monthLabels.map((name, i) => ({ name, newSignups: counts[i] }));
  };


  useEffect(() => {
    if (!filteredBrokerages || filteredBrokerages.length === 0) {
        setProcessedData({ activeBrokerages: 0, inactiveBrokerages: 0, newBrokeragesByMonth: [],
                           brokersPerBrokerage: [], totalBrokers: 0 });
        return;
    }

    const active = filteredBrokerages.filter(b => b.is_active_subscription).length;
    const inactive = filteredBrokerages.length - active;
    
    // [UPDATED] Call the new function
    const newBrokeragesByMonth = processNewBrokeragesByMonth(filteredBrokerages);

    const brokersPerBrokerage = filteredBrokerages.map(b => ({
      name: b.name?.substring(0, 15) || b.code || 'Unknown',
      brokers: b.active_brokers?.length || 0
    })).sort((a, b) => b.brokers - a.brokers).slice(0, 15);

    const totalBrokers = filteredBrokerages.reduce((total, b) => total + (b.active_brokers?.length || 0), 0);

    setProcessedData({ activeBrokerages: active, inactiveBrokerages: inactive, newBrokeragesByMonth,
                       brokersPerBrokerage, totalBrokers });
  }, [filteredBrokerages]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
    setSortConfig({ key, direction });
  };

  const chartOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' }, title: { display: false } }, scales: { y: { beginAtZero: true, ticks: { precision: 0 } }}};

  const pieData = { labels: ['Active', 'Inactive'], datasets: [{ data: [processedData.activeBrokerages, processedData.inactiveBrokerages], backgroundColor: ['#36A2EB', '#FF6384']}]};
  
  // [REMOVED] "Previous Month" data from bar chart
  const barData = { labels: processedData.brokersPerBrokerage.map(item => item.name), datasets: [ { label: 'Current Brokers', data: processedData.brokersPerBrokerage.map(item => item.brokers), backgroundColor: '#4BC0C0' } ]};
  
  // [UPDATED] Line chart data to use new processed data
  const lineData = { labels: processedData.newBrokeragesByMonth.map(item => item.name), datasets: [{ label: 'New Sign-ups', data: processedData.newBrokeragesByMonth.map(item => item.newSignups), borderColor: '#9966FF', backgroundColor: 'rgba(153,102,255,0.2)', tension: 0.4, fill: true }]};

  return (
    <div className="tab-content">
      <div className="stats-overview">
        <div className="stat-card"><h3>Total Found</h3><p className="stat-value">{filteredBrokerages.length}</p></div>
        <div className="stat-card"><h3>Active Subscriptions</h3><p className="stat-value">{processedData.activeBrokerages}</p></div>
        <div className="stat-card"><h3>Inactive Subscriptions</h3><p className="stat-value">{processedData.inactiveBrokerages}</p></div>
        <div className="stat-card"><h3>Total Active Brokers</h3><p className="stat-value">{processedData.totalBrokers}</p></div>
      </div>

      <div className="charts-row">
        <div className="chart-container"><h3>Subscription Status</h3><div className="chart-wrapper"><Pie data={pieData} options={chartOptions} /></div></div>
        <div className="chart-container"><h3>Brokers per Brokerage (Top 15)</h3><div className="chart-wrapper"><Bar data={barData} options={chartOptions} /></div></div>
      </div>
      
      {/* [UPDATED] Changed title to be more accurate */}
      <div className="chart-container full-width"><h3>New Brokerage Sign-ups (Last 12 Months)</h3><div className="chart-wrapper"><Line data={lineData} options={chartOptions} /></div></div>
      
      {/* [REMOVED] Entire Broker Growth/Decline chart container */}

      <div className="table-container">
        <div className="table-header">
            <h3>Brokerages Overview</h3>
            <div>
                <input type="search" className="search-input" placeholder="Search name/code..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                </select>
            </div>
        </div>
        <table className="data-table">
          <thead><tr>
            <th onClick={() => requestSort('name')} className={sortConfig.key === 'name' ? 'sorted' : ''}>Name {getSortIndicator('name', sortConfig)}</th>
            <th onClick={() => requestSort('is_active_subscription')} className={sortConfig.key === 'is_active_subscription' ? 'sorted' : ''}>Status {getSortIndicator('is_active_subscription', sortConfig)}</th>
            <th onClick={() => requestSort('active_brokers.length')} className={sortConfig.key === 'active_brokers.length' ? 'sorted' : ''}>Brokers {getSortIndicator('active_brokers.length', sortConfig)}</th>
            <th onClick={() => requestSort('next_renew_date')} className={sortConfig.key === 'next_renew_date' ? 'sorted' : ''}>Renewal {getSortIndicator('next_renew_date', sortConfig)}</th>
            <th onClick={() => requestSort('contact_number')} className={sortConfig.key === 'contact_number' ? 'sorted' : ''}>Contact {getSortIndicator('contact_number', sortConfig)}</th>
          </tr></thead>
          <tbody>
            {sortedAndFilteredBrokerages.length === 0 ? (
              <tr><td colSpan="5" className="empty-data-cell">No brokerages found matching criteria.</td></tr>
            ) : (
              sortedAndFilteredBrokerages.map((b) => (
              <tr key={b._id || b.id}>
                <td>{b.name || 'Unknown'}</td>
                <td><span className={`status-badge ${b.is_active_subscription ? 'active' : 'inactive'}`}>{b.is_active_subscription ? 'Active' : 'Inactive'}</span></td>
                
                {/* [REMOVED] Broker change indicator span */}
                <td>{b.active_brokers?.length || 0}</td>

                <td>{b.next_renew_date ? new Date(b.next_renew_date).toLocaleDateString() : 'N/A'}</td>
                <td>{b.contact_number || 'N/A'}</td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default BrokerageTab;